import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { FileText, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

const DEFAULT_TEMPLATES = {
  default_executive_summary_template: `Thank you for the opportunity to present our proposal for professional janitorial services at {{facility_name}}. We have carefully reviewed your facility during our walkthrough and have prepared a comprehensive cleaning plan tailored to your specific needs.

Our proposal includes complete janitorial services for your {{square_footage}} square foot facility, covering {{zone_count}} distinct service areas. We are confident that our professional team and quality-focused approach will exceed your expectations.`,

  default_why_choose_us_template: `**Quality Assurance:** We maintain the highest cleaning standards through regular inspections and quality control procedures.

**Professional Team:** Our staff is trained, background-checked, and committed to delivering exceptional service.

**Responsive Support:** A dedicated account manager ensures prompt communication and rapid issue resolution.

**Flexible Service:** We work around your schedule to minimize disruption to your operations.

**Proven Track Record:** Years of experience serving facilities throughout the region with consistent 5-star results.`,

  default_billing_terms_template: `Monthly invoices will be issued on the first business day of each month for services provided. Payment is due within 30 days of invoice date. We accept check, ACH, or credit card payments.`,

  default_service_term_template: `This agreement is for an initial term of 12 months, with automatic month-to-month renewal thereafter unless either party provides written notice of termination.`,

  default_cancellation_notice_template: `Either party may terminate this agreement with 30 days written notice. No penalty for cancellation after initial term.`,

  default_acceptance_text_template: `By signing below, Client accepts the terms and pricing outlined in this proposal and authorizes {{company_name}} to begin services on the projected start date.`
};

export function CompanyTemplatesUI() {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState({
    default_executive_summary_template: '',
    default_why_choose_us_template: '',
    default_billing_terms_template: '',
    default_service_term_template: '',
    default_cancellation_notice_template: '',
    default_acceptance_text_template: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api('/api/settings/company');
      setTemplates({
        default_executive_summary_template: data.default_executive_summary_template || DEFAULT_TEMPLATES.default_executive_summary_template,
        default_why_choose_us_template: data.default_why_choose_us_template || DEFAULT_TEMPLATES.default_why_choose_us_template,
        default_billing_terms_template: data.default_billing_terms_template || DEFAULT_TEMPLATES.default_billing_terms_template,
        default_service_term_template: data.default_service_term_template || DEFAULT_TEMPLATES.default_service_term_template,
        default_cancellation_notice_template: data.default_cancellation_notice_template || DEFAULT_TEMPLATES.default_cancellation_notice_template,
        default_acceptance_text_template: data.default_acceptance_text_template || DEFAULT_TEMPLATES.default_acceptance_text_template
      });
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates(DEFAULT_TEMPLATES);
    }
  };

  const handleChange = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api('/api/settings/company', {
        method: 'PUT',
        body: JSON.stringify(templates)
      });
      toast.success('Proposal templates saved successfully');
    } catch (error) {
      toast.error('Failed to save templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Reset all templates to defaults? This cannot be undone.')) {
      setTemplates(DEFAULT_TEMPLATES);
      toast.info('Templates reset to defaults (not saved yet)');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-[Space_Grotesk] font-semibold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Proposal Content Templates
          </h2>
          <p className="text-muted-foreground">Default content used when generating new proposals</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleResetToDefaults} disabled={loading}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-templates-button">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save Templates'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Available Variables</CardTitle>
          <CardDescription>Use these placeholders in your templates - they will be replaced with actual data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2 text-sm font-mono bg-gray-50 p-4 rounded-lg">
            <div><code>{'{{company_name}}'}</code></div>
            <div><code>{'{{facility_name}}'}</code></div>
            <div><code>{'{{square_footage}}'}</code></div>
            <div><code>{'{{zone_count}}'}</code></div>
            <div><code>{'{{monthly_price}}'}</code></div>
            <div><code>{'{{frequency}}'}</code></div>
            <div><code>{'{{contact_name}}'}</code></div>
            <div><code>{'{{start_date}}'}</code></div>
            <div><code>{'{{representative_name}}'}</code></div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="space-y-6">
        {/* Executive Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Executive Summary Template</CardTitle>
            <CardDescription>Opening section of the proposal - introduce your company and the project</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_executive_summary_template}
              onChange={(e) => handleChange('default_executive_summary_template', e.target.value)}
              rows={6}
              className="font-sans"
              data-testid="exec-summary-template"
            />
          </CardContent>
        </Card>

        {/* Why Choose Us */}
        <Card>
          <CardHeader>
            <CardTitle>Why Choose Us Template</CardTitle>
            <CardDescription>Highlight your company's strengths and differentiators</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_why_choose_us_template}
              onChange={(e) => handleChange('default_why_choose_us_template', e.target.value)}
              rows={8}
              className="font-sans"
              data-testid="why-choose-template"
            />
          </CardContent>
        </Card>

        {/* Billing Terms */}
        <Card>
          <CardHeader>
            <CardTitle>Billing Terms Template</CardTitle>
            <CardDescription>Default payment and invoicing terms</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_billing_terms_template}
              onChange={(e) => handleChange('default_billing_terms_template', e.target.value)}
              rows={3}
              className="font-sans"
              data-testid="billing-terms-template"
            />
          </CardContent>
        </Card>

        {/* Service Term */}
        <Card>
          <CardHeader>
            <CardTitle>Service Term Template</CardTitle>
            <CardDescription>Default contract duration and renewal terms</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_service_term_template}
              onChange={(e) => handleChange('default_service_term_template', e.target.value)}
              rows={3}
              className="font-sans"
              data-testid="service-term-template"
            />
          </CardContent>
        </Card>

        {/* Cancellation Notice */}
        <Card>
          <CardHeader>
            <CardTitle>Cancellation Notice Template</CardTitle>
            <CardDescription>Default cancellation policy and notice period</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_cancellation_notice_template}
              onChange={(e) => handleChange('default_cancellation_notice_template', e.target.value)}
              rows={2}
              className="font-sans"
              data-testid="cancellation-template"
            />
          </CardContent>
        </Card>

        {/* Acceptance Text */}
        <Card>
          <CardHeader>
            <CardTitle>Acceptance Text Template</CardTitle>
            <CardDescription>Text above signature line</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={templates.default_acceptance_text_template}
              onChange={(e) => handleChange('default_acceptance_text_template', e.target.value)}
              rows={2}
              className="font-sans"
              data-testid="acceptance-template"
            />
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading} size="lg" data-testid="save-templates-bottom">
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : 'Save All Templates'}
        </Button>
      </div>
    </div>
  );
}
